import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from "@mui/material";
import { type ChangeEvent, type FC, useCallback, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { formatDate, fromISO8601_DATE, type INTERVAL } from "../../../../utils/iso8601";
import { useFinalOccurrence } from "../../../event/hooks/use-number-of-repeats";
import type { MomentFormMoment } from "../../../moment/types/moment-form-moment";
import type { ScheduleFormV2LayoutProps } from "./types";

export const ScheduleRepeatToggle: FC<Omit<ScheduleFormV2LayoutProps, "showValidationErrors">> = ({
  disabled,
}) => {
  const { control, setValue, getValues } = useFormContext<MomentFormMoment>();

  useWatch({ control, name: ["schedule.event.calendar"] });
  const calendar = getValues("schedule.event.calendar");

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // If it is a calendar (i.e custom) event, then we always want to send on
      // the initial event.
      // For non-calendar (i.e automated) events, we want to send on the initial
      // event if the user has not checked the repeat checkbox
      setValue("schedule.event_initial", calendar ? true : !event.target.checked);
      setValue("schedule.event_repeat", event.target.checked);
    },
    [calendar, setValue],
  );

  return (
    <Box width="100%">
      <Paper sx={{ p: 2 }} variant="outlined">
        <Controller
          name="schedule.event_repeat"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 0 }}
              control={<Switch disabled={disabled} checked={field.value} onChange={onChange} />}
              label="Repeating Communication"
            />
          )}
        />
        <ScheduleRepeatText disabled={disabled} />
      </Paper>
    </Box>
  );
};

const ScheduleRepeatText: FC<{ disabled: boolean }> = ({ disabled }) => {
  const { control, getValues } = useFormContext<MomentFormMoment>();
  useWatch({
    control,
    name: [
      "schedule.event.repeat",
      "schedule.event_repeat",
      "schedule.event.number_of_repeats",
      "schedule.event.date",
    ],
  });
  const [repeat, event_repeat, number_of_repeats, startDate] = getValues([
    "schedule.event.repeat",
    "schedule.event_repeat",
    "schedule.event.number_of_repeats",
    "schedule.event.date",
  ]);

  const { loading: repeatInfoLoading, data: repeatInfo } = useFinalOccurrence(
    startDate,
    number_of_repeats,
    repeat,
  );

  const text = useMemo(() => {
    switch (repeat) {
      case "1 years" as INTERVAL:
      case "1 year" as INTERVAL:
        return "Repeats annually";
      case "1 months" as INTERVAL:
      case "1 month" as INTERVAL:
      case "1 mon" as INTERVAL:
        return "Repeats monthly";
      case "1 weeks" as INTERVAL:
      case "1 week" as INTERVAL:
      case "7 days" as INTERVAL:
        return "Repeats weekly";
      default:
        return `Repeats every ${repeat}`;
    }
  }, [repeat]);

  const finalText = useMemo(() => {
    if (repeatInfoLoading) return null;

    if (repeatInfo === null || repeatInfo === undefined) return null;

    if (number_of_repeats === 100) return "Repeats indefinitely";

    return `Final occurrence ${formatDate(fromISO8601_DATE(repeatInfo))}`;
  }, [repeatInfoLoading, repeatInfo, number_of_repeats]);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography
        variant="subtitle1"
        sx={{ color: disabled || !event_repeat ? "text.disabled" : "text.primary" }}
      >
        {text}
      </Typography>
      {finalText && (
        <Typography
          variant="subtitle1"
          sx={{ color: disabled || !event_repeat ? "text.disabled" : "text.primary" }}
        >
          ({finalText})
        </Typography>
      )}
    </Stack>
  );
};
